import type { PageType, StarterPageKind } from "../schema/index.js";
import {
  listStarterTemplates,
  type VerticalStarterTemplate,
} from "./index.js";

export interface PageTemplatePage {
  id: string;
  title: string;
  description: string;
  kind: StarterPageKind;
  pageType: PageType;
  sectionTypes: string[];
}

export interface PageTemplateThumbnail {
  eyebrow: string;
  title: string;
  accent: string;
  sections: string[];
}

export interface PageTemplate {
  id: string;
  vertical: string;
  name: string;
  description: string;
  tags: string[];
  pageCount: number;
  preview: string;
  thumbnail: PageTemplateThumbnail;
  pages: PageTemplatePage[];
}

const THUMBNAIL_ACCENTS: Record<string, string> = {
  dental: "from-sky-500/20 via-cyan-500/10 to-white",
  "yoga-fitness": "from-emerald-500/20 via-lime-500/10 to-white",
  "salon-spa": "from-rose-500/20 via-pink-500/10 to-white",
  coaching: "from-amber-500/20 via-orange-500/10 to-white",
  legal: "from-slate-500/20 via-zinc-500/10 to-white",
  "home-services": "from-blue-500/20 via-indigo-500/10 to-white",
  restaurant: "from-red-500/20 via-orange-500/10 to-white",
  "real-estate": "from-cyan-500/20 via-sky-500/10 to-white",
  medical: "from-teal-500/20 via-cyan-500/10 to-white",
  ecommerce: "from-fuchsia-500/20 via-violet-500/10 to-white",
  education: "from-indigo-500/20 via-blue-500/10 to-white",
  photography: "from-neutral-700/20 via-neutral-500/10 to-white",
};

function buildPreview(template: VerticalStarterTemplate): string {
  const firstPage = template.pages[0];
  if (!firstPage) {
    return `${template.name} starter`;
  }

  const sectionTypes = firstPage.content
    .map((block) => block.type)
    .filter(Boolean)
    .slice(0, 3);

  return sectionTypes.length > 0
    ? `${firstPage.title}: ${sectionTypes.join(" · ")}`
    : `${template.name} starter`;
}

function buildThumbnail(template: VerticalStarterTemplate): PageTemplateThumbnail {
  const firstPage = template.pages[0];
  const sections = firstPage?.content
    .map((block) => block.type)
    .filter(Boolean)
    .slice(0, 4) ?? [];

  return {
    eyebrow: template.vertical.replaceAll("-", " "),
    title: firstPage?.title ?? template.name,
    accent: THUMBNAIL_ACCENTS[template.vertical] ?? "from-primary/20 via-primary/5 to-white",
    sections,
  };
}

function mapTemplate(template: VerticalStarterTemplate): PageTemplate {
  return {
    id: template.id,
    vertical: template.vertical,
    name: template.name,
    description: template.description,
    tags: template.tags,
    pageCount: template.pages.length,
    preview: buildPreview(template),
    thumbnail: buildThumbnail(template),
    pages: template.pages.map((page) => ({
      id: page.id,
      title: page.title,
      description: page.description,
      kind: page.kind,
      pageType: page.pageType,
      sectionTypes: page.content.map((block) => block.type).filter(Boolean),
    })),
  };
}

export function listPageTemplates(): PageTemplate[] {
  return listStarterTemplates().map(mapTemplate);
}

export function getPageTemplateByVertical(vertical: string): PageTemplate | undefined {
  return listPageTemplates().find((template) => template.vertical === vertical);
}
